<script setup lang="ts">
import { ref, computed } from "vue";
import { bemBuilder } from "@chatfood/core-utils";
import {
  OrgLoadingModal,
  SatLanguageSwitcher,
  type ISatLanguageSwitcherEmits,
} from "@chatfood/design-system";
import { useLocale } from "~/composables/use-locale";
import { useBusinessStore } from "~/stores/business";
import { useCookie } from "#imports";

const css = bemBuilder("switch-language");

const business = useBusinessStore();

const { setLocaleCookie, t, availableLocales, isLocale } = useLocale();
const { value: cookieLang } = useCookie("i18n_redirected");
type Locale = (typeof availableLocales)["value"][0];

const selectedLanguage = ref(cookieLang ?? business.locale);
const isRefreshingLanguage = ref(false);

const languages = computed<Locale[]>(() => {
  return (business.languages as Locale[]).filter((lang) =>
    availableLocales.value.includes(lang),
  );
});

const findSelectedLanguage = computed<Locale | undefined>(() =>
  languages.value.find((obj) => obj === selectedLanguage.value),
);

const switchLanguage = (langId: ISatLanguageSwitcherEmits["select"][0]) => {
  const isSupported = isLocale(langId);

  if (!isSupported) return;
  isRefreshingLanguage.value = true;

  setLocaleCookie(langId);
  selectedLanguage.value = langId;
  window.location.reload();
};

const displayComponent = computed(() => languages.value.length > 1);
</script>

<template>
  <div :class="css('')">
    <SatLanguageSwitcher
      v-if="displayComponent"
      :languages="languages"
      :selected="findSelectedLanguage"
      aligment="right"
      color="color-text-secondary"
      :class="css('button')"
      :icon-size="21"
      icon-name="languages"
      @select="(key) => switchLanguage(key)"
    />
    <OrgLoadingModal
      v-model="isRefreshingLanguage"
      :primary-text="t('components.switch_language.warn_title')"
      :secondary-text="t('components.switch_language.warn_description')"
    />
  </div>
</template>

<style lang="scss" scoped>
.switch-language {
  display: flex;
  align-items: center;
  margin: 0 var(--spacing-xs);

  &__button {
    padding: var(--spacing-xs);
    width: 35px;
    height: 35px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
